'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * The pinned foot of the enclosing overlay (`Dialog` / `Sheet`): the row below
 * the overlay's one scroller, at the visible bottom of its panel. Each overlay
 * publishes its foot element through this context, the way the app shell
 * publishes its top bar (`TopbarSlot`). `undefined` = not inside an overlay;
 * `null` = inside one whose foot has not mounted yet (its first commit).
 */
export const OverlayFootContext = createContext<HTMLElement | null | undefined>(undefined);

/**
 * Seats its children in the enclosing overlay's pinned foot, so an act row
 * nested deep in an overlay's body (the access panel's Add as / Apply) sits at
 * the visible bottom however far the body scrolls: no second scroll container,
 * and no sticky row with the scroller's padding showing content beneath it.
 * Outside an overlay (a page host) the children render in place.
 *
 * One act row per foot: a Dialog that passes its own `footer` shares the row,
 * so a body seated in such a dialog keeps its act inline instead.
 */
export function OverlayFoot({ children }: { children: ReactNode }) {
  const slot = useContext(OverlayFootContext);
  if (slot === undefined) return <>{children}</>;
  return slot ? createPortal(children, slot) : null;
}
